// Wire-format types and API contract for Management Guidelines (PRD 07).
// Mirrors the shape used by `diseaseCodeApi` so adapter/error patterns stay
// consistent across features. See feature plan: 1 guideline = 1 grade, GID is
// system-assigned `G-###`, blocks are stored as a flat array per PRD §4.3.

export type GuidelineGrade = 'G1' | 'G2' | 'G3';

export const GUIDELINE_GRADES: readonly GuidelineGrade[] = ['G1', 'G2', 'G3'] as const;

export type GuidelineStatus = 'DRAFT' | 'PUBLISHED';

// PRD §4.3 — single block shape covering text/image/video. `text` carries
// rich-text HTML for `text` blocks; for `image`/`video` it's the description
// string (visible only when `showText` is true).
export type ContentBlockType = 'text' | 'image' | 'video';

export interface ContentBlock {
  id: number;
  type: ContentBlockType;
  title: string;
  text: string;
  showText: boolean;
  // IMAGE ONLY — the uploaded image's storage URL (a base64 dataUrl in mock mode).
  // SHCT-648: video blocks no longer set this. Direct video upload is out of scope
  // (ADM.018 / ADM.026) and PRD 07 §4.4 says "영상 블록은 dataUrl을 쓰지 않고 url 필드만 사용".
  // Kept optional-and-untyped-per-kind because a guideline saved before that decision
  // may still carry a video `dataUrl` on the wire; nothing reads it any more.
  dataUrl?: string;
  // Video only: the external URL (YouTube / Vimeo). This is the video block's single source.
  url?: string;
}

// PRD §4.1 — list row shape rendered in the table. Mirrors prototype
// `MockData.managementGuidelines` plus the explicit category/subArea pair so
// the edit panel can pre-fill the cascading selects without a second fetch.
export interface ManagementGuideline {
  // 'G-###' (PRD §4.4) — system-assigned, never editable.
  gid: string;
  name: string;
  grade: GuidelineGrade;
  // PRO-tree leaf item name (e.g. '지속적인 우울감'). PRD §4.1. Display only —
  // see `linkedItemId` for the key to join on.
  linkedItem: string;
  // SHCT-220 — the bound PRO leaf item's unique id, and the ONLY safe key to
  // resolve the linkage with. Item names are not unique (uniqueness holds over
  // the whole 영역/하위영역/항목/등급 tuple), so two items sharing a name in
  // different sub-areas are indistinguishable by `linkedItem` alone and a
  // name-keyed lookup silently resolves both to whichever row comes first.
  // Null exactly where `linkedItem` is absent/unreadable — such a row matches
  // nothing, which is the same outcome the name lookup gave it.
  linkedItemId: number | null;
  // Auto-derived from `category` by matching `proItems[].area`. PRD §3.2.
  pids: string[];
  status: GuidelineStatus;
  // 'YYYY.MM.DD' (KST). PRD §4.4. Null while DRAFT.
  deployedAt: string | null;
  // FEATURE_028 (SHCT-590) — server-side "changed but not yet deployed" marker that
  // drives the yellow 수정 badge beside the GID. Optional so a mock or an older cached
  // payload without the field still type-checks; missing = false.
  dirty?: boolean;
  // FEATURE_030 — true when this guideline's bound PRO item can no longer be resolved
  // (the binding is missing, or the item it names has been deleted). Such a guideline
  // reaches no patient: the patient bundle is assembled by walking the bound item, so
  // an unresolvable one is skipped silently — no error, no log, nothing on screen.
  // Derived by the server on every read rather than stored, so it can never disagree
  // with reality. Optional so an older cached payload still type-checks; missing = false.
  orphaned?: boolean;
}

// Detail returned when opening the edit panel — adds the cascade context and
// the block array. Split from `ManagementGuideline` so the list response can
// stay light once BE wires the real endpoint.
export interface ManagementGuidelineDetail extends ManagementGuideline {
  category: string;
  subArea: string;
  blocks: ContentBlock[];
}

// PRD §3.2 / §4.2 — required fields validated server-side. `pids` is derived
// from `category` by BE, so FE doesn't send it.
export interface CreateGuidelineInput {
  name: string;
  grade: GuidelineGrade;
  category: string;
  subArea: string;
  linkedItem: string;
  blocks: ContentBlock[];
  // DRAFT for "Save Draft", PUBLISHED for "Save".
  status: GuidelineStatus;
}

export type UpdateGuidelineInput = Partial<CreateGuidelineInput>;

// ── PRO tree wire shape — GET /admin/pro/tree (PRO Item Design) ──────────────
// The nested 3-depth tree returned by the backend: 영역(domain) → 하위영역
// (sub-domain) → 항목(item). This is the single read source for the cascade
// select (see treeToTopicNodes in utils/topic-tree).
export interface ProTreeItem {
  id: number;
  // 'P-###' display code minted by the backend per item.
  pid: string;
  name: string;
  // Wire enum code (e.g. DRAFT/PUBLISHED); the FE maps it to a Korean label
  // when displayed. Not read by the management-guidelines picker.
  status: string;
  subDomainId: number;
}

export interface ProTreeSubDomain {
  id: number;
  name: string;
  sortOrder: number;
  items: ProTreeItem[];
}

export interface ProTreeDomain {
  id: number;
  name: string;
  sortOrder: number;
  subDomains: ProTreeSubDomain[];
}

export interface ProTree {
  domains: ProTreeDomain[];
}

// FE-derived view-model (NOT a wire shape): one node per (category, sub-area)
// pair, produced from ProTree by treeToTopicNodes. Powers the cascading select.
export interface ProTopicItem {
  name: string;
}

export interface ProTopicNode {
  id: number;
  category: string;
  subArea: string;
  items: ProTopicItem[];
}

// SHCT-639 — `ProItemPidRef` removed with the PID auto-link field. `ProTreeItem.pid`
// stays on the wire shape (the backend still sends it); nothing in this feature
// renders it any more.

export type ManagementGuidelineApiErrorCode =
  | 'NAME_REQUIRED'
  | 'NAME_TOO_LONG'
  | 'CATEGORY_REQUIRED'
  | 'SUB_AREA_REQUIRED'
  | 'LINKED_ITEM_REQUIRED'
  | 'GRADE_REQUIRED'
  // SHCT-777 — PRD 07 §3 makes (영역/하위영역/항목 + Grade) the key of the 관리지침↔PRO 1:1 매칭:
  // "하나의 관리지침은 하나의 항목·단계(Grade)에만 매칭된다". A second guideline on the same
  // combination breaks that invariant, and PRD 04 §9 then cannot honour its own
  // "1:1로 연결된 1개를 읽기전용 표시". The server owns the check (only it sees every row, and only
  // it can make the check atomic); this is the code it raises.
  | 'GUIDELINE_DUPLICATE'
  | 'STATUS_TRANSITION_FORBIDDEN'
  | 'NOT_FOUND'
  | 'INSUFFICIENT_PERMISSION'
  | 'NETWORK'
  | 'UNKNOWN';

export class ManagementGuidelineApiError extends Error {
  readonly code: ManagementGuidelineApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: Record<string, unknown>;

  constructor(
    code: ManagementGuidelineApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'ManagementGuidelineApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, ManagementGuidelineApiError.prototype);
  }
}

// Result of DELETE — BE auto-unlinks affected PRO items and reports the count
// so the success toast can mention it (PRD §3.3).
export interface DeleteGuidelineResult {
  unlinkedPidCount: number;
}

export interface ManagementGuidelineApi {
  // PRD §3.1 — full list, unpaginated for MVP. Search/sort are client-side
  // until BE confirms server-side support.
  list(): Promise<ManagementGuideline[]>;

  // PRD §3.2 — opens the edit panel. Returns blocks + cascade pair so the form
  // can hydrate without an extra fetch.
  get(gid: string): Promise<ManagementGuidelineDetail>;

  create(input: CreateGuidelineInput): Promise<ManagementGuidelineDetail>;

  // PRD §3.4 — PUBLISHED → DRAFT is forbidden by BE.
  update(gid: string, input: UpdateGuidelineInput): Promise<ManagementGuidelineDetail>;

  // PRD §3.3 — cascades to PRO Item Design (auto-unlink).
  remove(gid: string): Promise<DeleteGuidelineResult>;

  // PRD §3.2 — the single whole-tree read behind the cascading select
  // (treeToTopicNodes), never a second tree request.
  getProTree(): Promise<ProTree>;

  // FEATURE_061 — upload an IMAGE block file; returns the stored URL to put in
  // `block.dataUrl`. Real BE uploads to GCS (POST /admin/management-guidelines/uploads → { url });
  // the mock returns a base64 dataUrl so dev works offline without the backend.
  // SHCT-648: the `kind` parameter is gone — video is URL-only, so image is the
  // only thing this endpoint ever receives (BE BlockMediaService accepts JPEG/PNG only).
  uploadMedia(file: File): Promise<string>;
}
